import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProfileService } from '../../../services/Api'
import { ShareService } from '../Utils/shareService'
import { PROFILE_TABS } from '../Common/profileConstants'

export const useProfile = () => {
  const navigate = useNavigate()
  
  // State management
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [activeTab, setActiveTab] = useState(PROFILE_TABS.OVERVIEW)
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "",
  })
  const [updateLoading, setUpdateLoading] = useState(false)
  const [updateSuccess, setUpdateSuccess] = useState(false)
  const [updateError, setUpdateError] = useState("")
  const [referralStats, setReferralStats] = useState(null)
  const [referrals, setReferrals] = useState([])
  const [referralRewards, setReferralRewards] = useState([])
  const [referralCode, setReferralCode] = useState("")
  const [referralLoading, setReferralLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedReferrals, setCopiedReferrals] = useState(false)
  const [shareError, setShareError] = useState("")
  const [savedProperties, setSavedProperties] = useState([])
  const [savedPropertiesLoading, setSavedPropertiesLoading] = useState(false)

  // Check authentication and fetch profile
  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    const userData = localStorage.getItem('userData')
    
    if (!token) {
      navigate('/login')
      return
    }

    // If we have user data in localStorage, set it immediately
    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData)
        setUser(parsedUserData)
        setFormData({
          first_name: parsedUserData.first_name,
          last_name: parsedUserData.last_name,
          phone_number: parsedUserData.phone_number,
          email: parsedUserData.email,
        })
      } catch (error) {
        console.error('Error parsing user data:', error)
      }
    }

    const fetchUserProfile = async () => {
      try {
        const response = await ProfileService.getProfile()
        if (response.success) {
          setUser(response.data)
          setFormData({
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            phone_number: response.data.phone_number,
            email: response.data.email,
          })
          // Update localStorage with fresh user data
          localStorage.setItem('userData', JSON.stringify(response.data))
        } else {
          setError("Failed to fetch profile data")
        }
        setLoading(false)
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          handleAuthError()
        } else {
          setError(err.response?.data?.message || "An error occurred while fetching your profile")
        }
        setLoading(false)
      }
    }

    fetchUserProfile()
  }, [navigate])

  // Fetch referral data when tab changes
  useEffect(() => {
    if (activeTab === PROFILE_TABS.REFERRALS && user) {
      const fetchReferralData = async () => {
        setReferralLoading(true)
        try {
          const referralData = await ProfileService.getReferralData()
          setReferralStats(referralData.stats)
          setReferrals(referralData.referrals)
          setReferralRewards(referralData.rewards)
          setReferralCode(referralData.code)
          setReferralLoading(false)
        } catch (error) {
          if (error.response?.status === 401 || error.response?.status === 403) {
            handleAuthError()
          } else {
            console.error("Error fetching referral data:", error)
          }
          setReferralLoading(false)
        }
      }
      fetchReferralData()
    }
    // Fetch saved properties when Saved tab is active
    if (activeTab === PROFILE_TABS.SAVED && user) {
      const fetchSavedProperties = async () => {
        setSavedPropertiesLoading(true)
        try {
          const response = await ProfileService.getSavedProperty()
          if (response.success) {
            setSavedProperties(response.data)
          } else {
            setSavedProperties([])
          }
        } catch (error) {
          setSavedProperties([])
        } finally {
          setSavedPropertiesLoading(false)
        }
      }
      fetchSavedProperties()
    }
  }, [activeTab, user])

  // Handlers
  const handleAuthError = () => {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("userData")
    navigate('/login')
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (updateError) setUpdateError("")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setUpdateLoading(true)
    setUpdateSuccess(false)
    setUpdateError("")

    try {
      const response = await ProfileService.updateProfile(formData)
      if (response.success) {
        setUser(response.data)
        setUpdateSuccess(true)
        setEditMode(false)
      } else {
        setUpdateError("Failed to update profile")
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleAuthError()
      } else {
        setUpdateError(err.response?.data?.message || "Failed to update profile")
      }
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("userData")
    navigate("/login")
  }

  const copyReferralCode = () => {
    const codeToUse = referralCode || user?.referral_code
    if (codeToUse) {
      ShareService.copyToClipboard(codeToUse)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const copyReferralCodeReferrals = () => {
    const codeToUse = referralCode || user?.referral_code
    if (codeToUse) {
      ShareService.copyToClipboard(codeToUse)
      setCopiedReferrals(true)
      setTimeout(() => setCopiedReferrals(false), 2000)
    }
  }

  const handleShare = async () => {
    try {
      const codeToUse = referralCode || user?.referral_code
      ShareService.shareViaWhatsApp(codeToUse)
      setShareError("")
    } catch (error) {
      setShareError("Error sharing referral code")
      console.error('Share error:', error)
    }
  }


  const handleSavedProperty = async (property_id) => {
    try {
      await ProfileService.toggleSaveProperty({ property_id });
      console.log('Property toggled:', property_id);
      // Refresh saved properties after toggling
      setSavedPropertiesLoading(true);
      try {
        const response = await ProfileService.getSavedProperty();
        if (response.success) {
          setSavedProperties(response.data);
        } else {
          setSavedProperties([]);
        }
      } catch (error) {
        setSavedProperties([]);
      } finally {
        setSavedPropertiesLoading(false);
      }
    } catch (error) {
      console.error('Error toggling property:', error);
    }
  };

  return {
    // State
    user,
    loading,
    error,
    activeTab,
    editMode,
    formData,
    updateLoading,
    updateSuccess,
    updateError,
    referralStats,
    referrals,
    referralRewards,
    referralCode,
    referralLoading,
    copied,
    copiedReferrals,
    shareError,
    savedProperties,
    savedPropertiesLoading,
    
    // Actions
    setActiveTab,
    setEditMode,
    handleChange,
    handleSubmit,
    handleLogout,
    copyReferralCode,
    copyReferralCodeReferrals,
    handleShare,
    handleSavedProperty
  }
}